import React, { Suspense, useState } from "react";
import { FiShare2 } from "react-icons/fi";
import { Modal } from "../Modal";
import { LoadingSpinner } from "../LoadingSpinner";

// The usage graph pulls in @xyflow/react — load it lazily so it costs nothing until opened.
const TypeUsageGraph = React.lazy(() => import("./TypeUsageGraph"));

/**
 * "Show connections" for a content type / any data: opens a clickable graph of WHERE the type is used —
 * the fragments it feeds, the dynamic pages that render it, the home dashboard, the search index. Lets an
 * editor answer "if I change this data, what shows it?" and jump straight to any of those places.
 */
const TypeConnectionsButton: React.FC<{
	typeKey: string;
	typeLabel?: string;
	language?: string;
	className?: string;
	label?: string;
	iconOnly?: boolean;
}> = ({ typeKey, typeLabel, language = "fr", className, label = "Connexions", iconOnly = false }) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}
				className={
					className ??
					"inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
				}
				title={`Voir où « ${typeLabel ?? typeKey} » est utilisé`}
			>
				<FiShare2 className="h-3.5 w-3.5" />
				{!iconOnly && label}
			</button>
			<Modal show={open} onHide={() => setOpen(false)} size="full85" centered dialogClassName="min-h-[80vh]">
				<Modal.Header closeButton>
					<Modal.Title className="text-lg font-semibold text-foreground">
						Connexions — {typeLabel ?? typeKey}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="flex min-h-0 flex-col bg-muted/30">
					<p className="mb-2 text-xs text-slate-500">
						Où cette donnée est utilisée : les fragments qu'elle alimente, les pages dynamiques qui l'affichent, le
						tableau de bord et la recherche. Cliquez sur un nœud pour l'ouvrir.
					</p>
					{open && (
						<Suspense
							fallback={
								<div className="flex flex-1 items-center justify-center p-10">
									<LoadingSpinner size="lg" />
								</div>
							}
						>
							<TypeUsageGraph typeKey={typeKey} language={language} />
						</Suspense>
					)}
				</Modal.Body>
			</Modal>
		</>
	);
};

export default TypeConnectionsButton;
